import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, UserPlus, UserCheck, MessageCircle, X, User, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import MembershipGuardModal, { canSendDM } from "../MembershipGuardModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 작성자 액션 팝오버 — PostCard에서 작성자 클릭 시 표시
 * Props:
 *  - open
 *  - onClose
 *  - authorId
 *  - authorName
 *  - authorAvatar (optional)
 *  - onOpenDM(authorId, authorName)
 */
export default function AuthorActionsPopover({
  open, onClose, authorId, authorName, authorAvatar, onOpenDM,
}) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dmGuard, setDmGuard] = useState(null); // null | "auth" | "dm_upgrade"
  const isSelf = user && authorId === user.id;
  const dmAllowed = canSendDM(user);

  useEffect(() => {
    if (!open || !authorId) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/api/follows/status/${authorId}`, { headers })
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [open, authorId, token]);

  const toggleFollow = async () => {
    if (!user) { toast.error("로그인이 필요합니다"); return; }
    if (isSelf) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/follows/${authorId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "실패");
      setStatus((s) => ({
        ...(s || {}),
        i_follow: data.following,
        followers_count: data.followers_count,
        mutual: data.following && s?.they_follow_me,
      }));
      toast.success(data.following ? "팔로우했어요 💞" : "팔로우를 취소했어요");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openDM = () => {
    if (!user) {
      setDmGuard("auth");
      return;
    }
    if (isSelf) return;
    if (!dmAllowed) {
      setDmGuard("dm_upgrade");
      return;
    }
    onClose?.();
    onOpenDM?.(authorId, authorName);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-stone-900/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
          data-testid="author-popover-backdrop"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            data-testid="author-popover"
          >
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center"
                aria-label="닫기"
              >
                <X className="w-4 h-4 text-stone-500" />
              </button>
              <div className="flex items-center gap-3">
                {authorAvatar ? (
                  <img src={authorAvatar.startsWith("http") ? authorAvatar : `${API_URL}${authorAvatar}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-burgundy-500 to-rose-500 text-white flex items-center justify-center font-bold text-2xl shadow-sm">
                    {(authorName || "익").charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-stone-900 truncate">{authorName || "익명"}</div>
                  {status && (
                    <div className="text-xs text-stone-500 mt-1 flex gap-3">
                      <span><b className="text-stone-700">{status.followers_count}</b> 팔로워</span>
                      <span><b className="text-stone-700">{status.following_count}</b> 팔로잉</span>
                      {status.mutual && <span className="text-burgundy-600 font-medium">서로 팔로우 중</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Actions */}
            <div className="p-5 space-y-2">
              {isSelf ? (
                <div className="text-center text-sm text-stone-500 py-2">나 자신의 글이에요 🌿</div>
              ) : (
                <>
                  <button
                    onClick={toggleFollow}
                    disabled={loading || !user}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-[15px] transition-colors disabled:opacity-50 ${
                      status?.i_follow
                        ? "bg-stone-100 text-stone-700 hover:bg-stone-200"
                        : "bg-burgundy-600 text-white hover:bg-burgundy-700"
                    }`}
                    data-testid="author-follow-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      status?.i_follow ? <><UserCheck className="w-4 h-4" /> 팔로잉</> : <><UserPlus className="w-4 h-4" /> 팔로우</>
                    )}
                  </button>
                  <button
                    onClick={openDM}
                    disabled={!user}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-[15px] border transition-colors disabled:opacity-50 ${
                      dmAllowed
                        ? "bg-white text-stone-700 border-stone-200 hover:bg-stone-50"
                        : "bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100"
                    }`}
                    data-testid="author-dm-btn"
                    title={dmAllowed ? "쪽지 보내기" : "정회원 전용 기능"}
                  >
                    {dmAllowed ? <MessageCircle className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />}
                    쪽지 보내기
                    {!dmAllowed && user && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1">정회원 전용</span>
                    )}
                  </button>
                  <button
                    onClick={() => { onClose?.(); navigate(`/timeline/${authorId}`); }}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-[15px] bg-white text-stone-700 border border-stone-200 hover:bg-stone-50"
                    data-testid="author-profile-btn"
                  >
                    <User className="w-4 h-4" />
                    감성 타임라인 보기
                  </button>
                  {status?.they_follow_me && !status?.mutual && (
                    <p className="text-[11px] text-stone-400 text-center pt-1">이 분이 회원님을 팔로우하고 있어요</p>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
      {/* 쪽지 보내기 권한 가드 */}
      <MembershipGuardModal
        open={!!dmGuard}
        reason={dmGuard || "dm_upgrade"}
        onClose={() => setDmGuard(null)}
      />
    </>
  );
}
